import * as React from 'react'
import * as st from './DownloadButton.module.scss'
import { Button, Text, ButtonProps } from '@dvui'
import { DownloadStatus } from '@shared'

const radius = 11;
const circumference = Math.round(Math.PI * radius * 2)
const getOffset = (val = 0) => Math.round(((100 - Math.min(val, 100)) / 100) * circumference)
const deeplinkClass = global.__NATIVEAPP__ ? '' : 'js-deeplinkable'

type DownloadIcon = {
  status?: DownloadStatus
  progress?: number
}

const DownloadIcon = ({ status, progress = 0 }: DownloadIcon) => {
  const lineColor = "#6A6F75";
  const lineHighlightColor = "#D8D8D8";
  const lineWidth = 2;
  const progressColor = "#F2F4F6";
  const strokeDashoffset = status === DownloadStatus.WAITING ? getOffset(75) : getOffset(progress);
  const transition = `strokeDashoffset 2s linear`;

  const showCircle = status && [
    DownloadStatus.DOWNLOADING,
    DownloadStatus.PAUSED,
    DownloadStatus.QUEUED,
    DownloadStatus.ERROR,
  ].includes(status)
  const circleColor = status === DownloadStatus.ERROR ? lineHighlightColor : lineColor
  const showProgress = status === DownloadStatus.DOWNLOADING || status === DownloadStatus.PAUSED || status === DownloadStatus.WAITING
  const showArrow = status === DownloadStatus.IDLE || status === DownloadStatus.PAUSED || status === DownloadStatus.QUEUED
  const showtray = (status === DownloadStatus.IDLE)

  return (
    <svg width="24" height="25" xmlns="http://www.w3.org/2000/svg">
      <g fillRule="nonzero" fill="none">
        {showtray && (
          <path
            stroke={lineHighlightColor}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M2.997 18.087v4.972h18.03v-4.972"
          />
        )}

        {showArrow && (
          <path
            d="M12 5v12M7.5 12.5L12 17M16.5 12.5L12 17"
            stroke={
              status === DownloadStatus.IDLE ? lineHighlightColor : lineColor
            }
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        )}

        {showCircle && (
          <circle
            stroke={circleColor}
            strokeWidth={lineWidth}
            fill="none"
            cx="12"
            cy="12"
            r="11"
          />
        )}

        {showProgress && (
          <circle
            stroke={progressColor}
            transform="rotate(-90 12 12)"
            strokeWidth={lineWidth}
            fill="none"
            cx="12"
            cy="12"
            r="11"
            strokeDasharray="69"
            strokeDashoffset="69"
            strokeLinecap={'butt'}
            style={{ strokeDashoffset, transition }}
          >
            {status === DownloadStatus.WAITING && (
              <animateTransform
                attributeName="transform"
                attributeType="XML"
                type="rotate"
                from="0 12 12"
                to="360 12 12"
                dur="1s"
                repeatCount="indefinite"
              ></animateTransform>
            )}
          </circle>
        )}
        {status === DownloadStatus.COMPLETED && (
          // TODO: Do we need this?
          // tick
          // <path
          //   d="M17.611 7.308a1 1 0 0 1 1.444 1.384l-9.583 10a1 1 0 0 1-1.492-.054l-3.75-4.524a1 1 0 0 1 1.54-1.276l3.034 3.66 8.807-9.19z"
          //   fill="#D8D8D8"
          // />

          // bin
          <g fill="#B2B7BB" fillRule="nonzero">
            <path d="M15 18V8a1 1 0 0 1 2 0v11a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V8a1 1 0 1 1 2 0v10h10z" />
            <path d="M7 8a1 1 0 1 1 2 0v7a1 1 0 0 1-2 0V8zM11 8a1 1 0 0 1 2 0v7a1 1 0 0 1-2 0V8zM1 5a1 1 0 1 1 0-2h18a1 1 0 0 1 0 2H1z" />
            <path d="M6.8 2v2c0 .552-.403 1-.9 1S5 4.552 5 4V1c0-.552.403-1 .9-1h7.2c.497 0 .9.448.9 1v3c0 .552-.403 1-.9 1s-.9-.448-.9-1V2H6.8z" />
          </g>
        )}
        {status === DownloadStatus.DOWNLOADING && (
          // pause
          <g fill="#D8D8D8">
            <path d="M10.3 16.012c0 .828-.335 1.054-.75 1.054-.414 0-.75-.226-.75-1.054V7.007C8.8 6.178 9.158 6 9.55 6s.75.178.75 1.007v9.005zM15.3 16.012c0 .828-.335 1.054-.75 1.054s-.75-.226-.75-1.054V7.007c0-.829.358-1.007.75-1.007s.75.178.75 1.007v9.005z" />
          </g>
        )}
        {status === DownloadStatus.ERROR && (
          <g fill="#D8D8D8">
            <path d="M13 13.024a.978.978 0 0 1-.975.976h-.05a.978.978 0 0 1-.975-.976V6.976c0-.537.439-.976.976-.976h.049c.536 0 .975.439.975.976v6.048zM11 16h2v2.04h-2z" />
          </g>
        )}
      </g>
    </svg>
  )
}

export type DownloadLabels = { [key in keyof typeof DownloadStatus]: string }

interface DownloadButtonProps {
  label: string
  downloadURL: string
  progress?: number
  status?: DownloadStatus
  onClick?: (event: React.MouseEvent) => void
}

export const DownloadButton = ({ progress, status = DownloadStatus.IDLE, downloadURL, onClick }: DownloadButtonProps) => (
  <a className={`${deeplinkClass} ${st.button}`} href={downloadURL} onClick={onClick}>
    <DownloadIcon {...{ progress, status }}/>
  </a>
)

type DownloadButtonWithLabelProps = DownloadButtonProps & ButtonProps

export const DownloadButtonWithLabel = ({ progress, status, label, downloadURL, ...rest }: DownloadButtonWithLabelProps) => {
  return (
    <Button href={downloadURL} {...rest} className={deeplinkClass} >
      <span className={st.buttonIcon}>
        <DownloadIcon {...{ progress, status }} />
      </span>
      <Text tag="span" string={label} />
    </Button>
  )
}
